/**
 * "Checklistor" inside work-order section 9 (Dokumentation).
 *
 * Pure presentational: props in, callbacks out. No Firebase, no app contexts.
 * Errors from the callbacks are shown inline as an Alert.
 *
 * Delete rule (spec §7): an item may be deleted by whoever created it, or by
 * anyone with `canDeleteAny`; a checklist by its creator or `canDeleteAny`.
 */
import React, { useState } from 'react';
import { Alert, Button, Form, ListGroup, Spinner } from 'react-bootstrap';
import { CheckSquare, Plus, Trash2, Square, Check } from 'lucide-react';
import { SectionTitle } from './SectionTitle';
import { ProjectChecklist, ChecklistItem } from '../../../../types/projects';
import ConfirmationModal from '../../../../Components/ConfirmationModal';
import { WORK_ORDER_LABELS } from '../../workOrderSections';
import './documentation.css';

export interface ChecklistsSectionProps {
    checklists: ProjectChecklist[];
    currentUserId: string;
    /** false → read-only (no add, toggle or delete). */
    canEdit: boolean;
    /** Admin: may delete any checklist or item. */
    canDeleteAny?: boolean;
    onAddChecklist: (title: string) => Promise<void>;
    onDeleteChecklist: (checklistKey: string) => Promise<void>;
    onAddItem: (checklistKey: string, text: string) => Promise<void>;
    onToggleItem: (checklistKey: string, itemKey: string, completed: boolean) => Promise<void>;
    onDeleteItem: (checklistKey: string, itemKey: string) => Promise<void>;
}

/**
 * The server stamps `createdBy` on items (guestApi gates item deletion on it)
 * but `ChecklistItem` in types/projects.ts does not declare it yet.
 */
type ChecklistItemWithAuthor = ChecklistItem & { createdBy?: string };

const MAX_TITLE_LENGTH = 200;
const MAX_ITEM_LENGTH = 500;

type DeleteTarget =
    | { type: 'checklist'; checklistKey: string }
    | { type: 'item'; checklistKey: string; itemKey: string };

function errorMessage(err: unknown, fallback: string): string {
    return err instanceof Error && err.message ? err.message : fallback;
}

function itemsOf(checklist: ProjectChecklist): ChecklistItemWithAuthor[] {
    if (!checklist.items) return [];
    return Object.entries(checklist.items)
        .map(([key, item]) => ({ ...item, key }))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

const ChecklistsSection: React.FC<ChecklistsSectionProps> = ({
    checklists,
    currentUserId,
    canEdit,
    canDeleteAny = false,
    onAddChecklist,
    onDeleteChecklist,
    onAddItem,
    onToggleItem,
    onDeleteItem,
}) => {
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [addingToChecklist, setAddingToChecklist] = useState<string | null>(null);
    const [newItemText, setNewItemText] = useState('');
    const [togglingItem, setTogglingItem] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const canDeleteChecklist = (checklist: ProjectChecklist) =>
        canEdit && (canDeleteAny || checklist.createdBy === currentUserId);
    const canDeleteItem = (item: ChecklistItemWithAuthor) =>
        canEdit && (canDeleteAny || (Boolean(item.createdBy) && item.createdBy === currentUserId));

    const run = async (action: () => Promise<void>, fallback: string): Promise<boolean> => {
        setSubmitting(true);
        setError(null);
        try {
            await action();
            return true;
        } catch (err) {
            setError(errorMessage(err, fallback));
            return false;
        } finally {
            setSubmitting(false);
        }
    };

    const handleCreateChecklist = async () => {
        const title = newTitle.trim();
        if (!title) return;
        if (await run(() => onAddChecklist(title), 'Kunde inte skapa checklistan')) {
            setNewTitle('');
            setShowCreateForm(false);
        }
    };

    const handleAddItem = async (checklistKey: string) => {
        const text = newItemText.trim();
        if (!text) return;
        if (await run(() => onAddItem(checklistKey, text), 'Kunde inte lägga till punkten')) {
            setNewItemText('');
            setAddingToChecklist(null);
        }
    };

    const handleToggleItem = async (checklistKey: string, item: ChecklistItem) => {
        setTogglingItem(item.key);
        setError(null);
        try {
            await onToggleItem(checklistKey, item.key, !item.completed);
        } catch (err) {
            setError(errorMessage(err, 'Kunde inte uppdatera punkten'));
        } finally {
            setTogglingItem(null);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        const target = deleteTarget;
        if (target.type === 'item') {
            await run(() => onDeleteItem(target.checklistKey, target.itemKey), 'Kunde inte ta bort punkten');
        } else {
            await run(() => onDeleteChecklist(target.checklistKey), 'Kunde inte ta bort checklistan');
        }
        setDeleteTarget(null);
    };

    return (
        <div className="wo-checklists">
            <SectionTitle
                icon={CheckSquare}
                title={WORK_ORDER_LABELS.checklists}
                count={checklists.length}
                level="sub"
                onAdd={canEdit && !showCreateForm ? () => setShowCreateForm(true) : undefined}
                addLabel="Ny checklista"
                adding={submitting}
            />

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-2">
                    {error}
                </Alert>
            )}

            {canEdit && showCreateForm && (
                <div className="wo-checklists__form mb-3 p-3 bg-light rounded">
                    <Form.Control
                        type="text"
                        placeholder="Titel på checklista..."
                        aria-label="Titel på checklista"
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        maxLength={MAX_TITLE_LENGTH}
                        disabled={submitting}
                        autoFocus
                    />
                    <small className={`d-block text-end mt-1 ${newTitle.length >= MAX_TITLE_LENGTH ? 'text-danger' : 'text-muted'}`}>
                        {newTitle.length}/{MAX_TITLE_LENGTH}
                    </small>
                    <div className="d-flex justify-content-end gap-2 mt-2">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => { setShowCreateForm(false); setNewTitle(''); }}
                            disabled={submitting}
                        >
                            Avbryt
                        </Button>
                        <Button variant="primary" size="sm" onClick={handleCreateChecklist} disabled={!newTitle.trim() || submitting}>
                            {submitting ? 'Skapar...' : 'Skapa'}
                        </Button>
                    </div>
                </div>
            )}

            {checklists.length === 0 && !showCreateForm && (
                <p className="text-muted mb-0">Inga checklistor ännu</p>
            )}

            {checklists.map((checklist) => {
                const items = itemsOf(checklist);
                const completedCount = items.filter((i) => i.completed).length;

                return (
                    <div key={checklist.key} className="wo-checklists__checklist border rounded mb-3">
                        <div className="p-3 bg-light d-flex justify-content-between align-items-center gap-2">
                            <div>
                                <strong>{checklist.title}</strong>
                                <span className="text-muted ms-2">({completedCount}/{items.length})</span>
                            </div>
                            {canDeleteChecklist(checklist) && (
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="wo-checklists__delete"
                                    aria-label={`Ta bort checklista ${checklist.title}`}
                                    onClick={() => setDeleteTarget({ type: 'checklist', checklistKey: checklist.key })}
                                    disabled={submitting}
                                >
                                    <Trash2 size={14} />
                                </Button>
                            )}
                        </div>
                        <ListGroup variant="flush">
                            {items.length === 0 && (
                                <ListGroup.Item className="text-muted">Inga punkter ännu</ListGroup.Item>
                            )}
                            {items.map((item) => (
                                <ListGroup.Item key={item.key} className="wo-checklists__item">
                                    <button
                                        type="button"
                                        className="wo-checklists__toggle"
                                        role="checkbox"
                                        aria-checked={item.completed}
                                        disabled={!canEdit || submitting || togglingItem === item.key}
                                        onClick={() => handleToggleItem(checklist.key, item)}
                                    >
                                        {togglingItem === item.key ? (
                                            <Spinner animation="border" size="sm" style={{ width: 20, height: 20 }} />
                                        ) : item.completed ? (
                                            <Check size={20} className="text-success" />
                                        ) : (
                                            <Square size={20} className="text-muted" />
                                        )}
                                        <span className={item.completed ? 'text-decoration-line-through text-muted' : ''}>
                                            {item.text}
                                        </span>
                                    </button>
                                    {canDeleteItem(item) && (
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="wo-checklists__delete text-danger p-0"
                                            aria-label={`Ta bort punkt ${item.text}`}
                                            onClick={() => setDeleteTarget({ type: 'item', checklistKey: checklist.key, itemKey: item.key })}
                                            disabled={submitting}
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    )}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                        {canEdit && (addingToChecklist === checklist.key ? (
                            <div className="p-2 border-top">
                                <Form.Control
                                    type="text"
                                    placeholder="Ny punkt..."
                                    aria-label="Ny punkt"
                                    value={newItemText}
                                    onChange={(e) => setNewItemText(e.target.value)}
                                    maxLength={MAX_ITEM_LENGTH}
                                    disabled={submitting}
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && newItemText.trim()) {
                                            e.preventDefault();
                                            handleAddItem(checklist.key);
                                        }
                                        if (e.key === 'Escape') {
                                            setAddingToChecklist(null);
                                            setNewItemText('');
                                        }
                                    }}
                                />
                                <small className={`d-block text-end mt-1 ${newItemText.length >= MAX_ITEM_LENGTH ? 'text-danger' : 'text-muted'}`}>
                                    {newItemText.length}/{MAX_ITEM_LENGTH}
                                </small>
                                <div className="d-flex justify-content-end gap-2 mt-2">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => { setAddingToChecklist(null); setNewItemText(''); }}
                                        disabled={submitting}
                                    >
                                        Avbryt
                                    </Button>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => handleAddItem(checklist.key)}
                                        disabled={!newItemText.trim() || submitting}
                                    >
                                        {WORK_ORDER_LABELS.add}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="p-2 border-top">
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="wo-checklists__add-item text-primary p-0"
                                    onClick={() => { setAddingToChecklist(checklist.key); setNewItemText(''); }}
                                    disabled={submitting}
                                >
                                    <Plus size={14} className="me-1" />
                                    Lägg till punkt
                                </Button>
                            </div>
                        ))}
                    </div>
                );
            })}

            <ConfirmationModal
                show={deleteTarget !== null}
                title={deleteTarget?.type === 'checklist' ? 'Ta bort checklista' : 'Ta bort punkt'}
                message={deleteTarget?.type === 'checklist' ? 'Vill du ta bort hela checklistan?' : 'Vill du ta bort denna punkt?'}
                variant="danger"
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteTarget(null)}
                loading={submitting}
            />
        </div>
    );
};

export default ChecklistsSection;
